
const ERROR_MESSAGES = Object.freeze({
  notExist: "Email doesn't exist in the database.",
  userExists: 'This email is already in use.',
  incorrect: 'Password is incorrect.',
  unverified: 'Email ownership not verified.',
  minBytes12: 'Password must be a minimum of 12 characters (no symbol, number or caps rule).',
  invalid: "No account exists with this email."
});

export function getFormErrorMessage(errorKey) {
  return ERROR_MESSAGES[errorKey] || errorKey;
}

export default class Form {
  static submitHandler = async (event) => {
      event.preventDefault();

      let form = event.target;
      let formData = new FormData(form);
      let data = {};
      let hasError = false;
      let submitButton = form.querySelector('button[type="submit"]') || form.querySelector('button');

      TIML.remove('small');
      form.querySelectorAll('.error').forEach(el => {
        el.classList.remove('error');
        el.style.borderColor = '';
        el.style.backgroundColor = '';
      });

      submitButton.disabled = true;
      submitButton.insertAdjacentHTML('afterend', '<div class="loader"></div>');

      formData.forEach((value, key) => {
        let el = form[key];

        // validate required fields
        if (el.getAttribute('required') && !el.value) {
          el.classList.add('error');
          const fieldError = document.createElement('small');
          fieldError.className = 'field-error';
          fieldError.innerText = "Field is required.";
          el.insertAdjacentElement('afterend', fieldError);
          hasError = true;
        }

        // custom validation field
        let validation = el.getAttribute('validation');

        if (validation) {
          let validations = validation.split(":");

          validations.forEach(rule => {
            console.log('validation rule: ', rule);
          });
        }

        // prepare for ajax
        data[key] = value;
      });

      if (hasError) {
        TIML.remove('.loader');
        submitButton.disabled = false;
        return;
      }

      // a form handler manually handles the form submission rather than using the action property.
      let handler = form.getAttribute('handler');

      // Public auth forms do not use the signed-in user's access token. The class
      // marker matters in staging, where public and administrative auth routes
      // share staging-auth.instant-auth.com.
      const isExternalAuth = form.classList.contains('auth-form') ||
        form.classList.contains('external-auth-form');
      
      let headers = {
        "Content-Type": "application/json",
        "Accept": "application/json"
      };

      // Only add authorization header for internal API calls
      if (!isExternalAuth) {
        try {
          await Auth.ensureSuiteBootstrap();
          const accessToken = await Auth.getValidAccessToken();
          headers["authorization"] = "Bearer " + accessToken;
        } catch (error) {
          Form.showSuiteBootstrapError(form, error, () => {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitButton);
            }
          });
          TIML.remove('.loader');
          submitButton.disabled = false;
          return;
        }
      }

      // if password, hash it now? or hash it on server?
      // or maybe both allowing me to add a salt on the server? is there any point?
      fetch (form.action, {
        headers: headers,
        method: form.getAttribute('method'),
        body: JSON.stringify(data)
      })
      .then(async response => {
        // Parse API payload even on 4xx/5xx so server-side validation can be handled in UI.
        let payload = {};
        try {
          payload = await response.json();
        } catch (e) {
          payload = {};
        }

        return {
          ok: response.ok,
          status: response.status,
          payload
        };
      })
      .then(async result => {
        let data = result.payload;
        console.log('Submission completed:', { success: Boolean(data.success), status: result.status });
        
        // dummy data
        //data.success = 0;
        //data.errors = [{key: "email", "errors": ["notExist"]}];
  
        // we always expect a status
        if (!data.hasOwnProperty('success')) {
          form.insertAdjacentHTML('beforeend', '<div><small>The server did not return a status. It must be broken. Sorry. Try again later!</small></div>');
        }
        
        // see if there's a handler for custom functionality
        let handlerResult;
        if (handler) {
          handlerResult = await globalThis[handler.split('.')[0]][handler.split('.')[1]](form, data);
        }
        
        // now check if there's a successRedirect
        if (data.success && handlerResult !== false) {
          let url = form.getAttribute('onSuccessRedirect');

          if (url) {
            // invalidate cache
            // eg: /surveys = "surveys". is this going to work for everything?
            const first = url.split('/').filter(Boolean)[0];
            console.log('before invalidation', API.cache);
            API.invalidateCache(first);
            console.log('after invalidation', API.cache);

            // match square bracket placeholders with data in the response.
            url = url.replace(/\[([^\]]+)\]/g, (match, key) => data[key] || match);

            if (Translations.langCode) {
              url = '/' + Translations.langCode + url;
            }

            TIML.handleRedirect(url);
          }
        }

        // display the errors in the form
        if (!data.success) {
          if (Array.isArray(data.errors) && data.errors.length) { // an array of errors. an array keeps them in order
            console.log('data.errors', data.errors);
            for (const errorObj of data.errors) { // {key: email, errors: ["notExist"]}
              console.log('errorObj', errorObj);
              let el = form.querySelector('[name="' + errorObj.key + '"]');
              if (!el && errorObj.key === 'credentials') {
                el = form.querySelector('[name="email"]') || form.querySelector('input');
              }
              if (el) {
                el.classList.add('error');
                el.style.borderColor = "red";
                el.style.backgroundColor = "#ffeded";
                for (const errorKey of errorObj.errors) { // key words of errors lets us use translations
                  el.insertAdjacentHTML('afterend', '<small>' + getFormErrorMessage(errorKey) + '</small>');
                }
              } else {
                for (const errorKey of errorObj.errors) {
                  form.insertAdjacentHTML('beforeend', '<div><small>' + getFormErrorMessage(errorKey) + '</small></div>');
                }
              }
            }
          } else if (!result.ok) {
            form.insertAdjacentHTML('beforeend', '<div><small>Request failed (' + result.status + '). Please try again.</small></div>');
          }
        }
      })
      .catch(error => {
        console.error('Submission Error data:', error);
        // show connection error, suggest trying again
      })
      .finally(() => {
        TIML.remove('.loader');
        submitButton.disabled = false;
      });
  }

  static showSuiteBootstrapError(form, error, onSuccess) {
    form.querySelector('.suite-bootstrap-error')?.remove();

    const errorBox = document.createElement('div');
    errorBox.className = 'suite-bootstrap-error';
    errorBox.setAttribute('role', 'alert');

    const message = document.createElement('p');
    message.textContent = error?.message ||
      'We could not finish setting up your Instant Suite account. Please try again.';

    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'suite-bootstrap-retry';
    retryButton.textContent = 'Retry account setup';
    retryButton.onclick = async () => {
      retryButton.disabled = true;
      retryButton.textContent = 'Retrying...';
      try {
        await Auth.ensureSuiteBootstrap();
        errorBox.remove();
        if (onSuccess) onSuccess();
      } catch (retryError) {
        message.textContent = retryError?.message ||
          'We still could not finish setting up your account. Please try again.';
        retryButton.disabled = false;
        retryButton.textContent = 'Retry account setup';
      }
    };

    errorBox.append(message, retryButton);
    form.appendChild(errorBox);
  }

  static labelHandler(el, action) {
    // note that this works when the label element encapsulates the span label and the input, because clicking label auto focuses input.
    var parent = el.parentElement;
    if (action == "blur" && !el.value) {
        parent.classList.add('untouched');
        parent.classList.remove('touched');
        return;
    }

    parent.classList.add('touched');
    parent.classList.remove('untouched');
  }
}
